/**
 * Local still composite: detect a solid background color on the subject still,
 * soft-matte it out, and place the still over a background plate (exact pixels).
 */

type Size = { width: number; height: number };
type Rgb = { r: number; g: number; b: number };

async function loadImage(src: string): Promise<{ img: HTMLImageElement; revoke: () => void }> {
  let url = src;
  let revoke = () => {};
  if (/^https?:\/\//i.test(src)) {
    const res = await fetch(src);
    if (!res.ok) throw new Error(`Falha ao baixar imagem (${res.status}).`);
    url = URL.createObjectURL(await res.blob());
    revoke = () => URL.revokeObjectURL(url);
  }
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Falha ao carregar imagem.'));
    el.src = url;
  });
  return { img, revoke };
}

function naturalSize(img: HTMLImageElement): Size {
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (!width || !height) throw new Error('Imagem sem dimensões.');
  return { width, height };
}

function coverRect(src: Size, box: Size): { x: number; y: number; width: number; height: number } {
  const c = Math.max(box.width / src.width, box.height / src.height);
  const width = src.width * c;
  const height = src.height * c;
  return { x: (box.width - width) / 2, y: (box.height - height) / 2, width, height };
}

function containRect(src: Size, box: Size): { x: number; y: number; width: number; height: number } {
  const c = Math.min(box.width / src.width, box.height / src.height);
  const width = src.width * c;
  const height = src.height * c;
  return { x: (box.width - width) / 2, y: (box.height - height) / 2, width, height };
}

function rgbDist(a: Rgb, b: Rgb): number {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function pixelAt(data: Uint8ClampedArray, w: number, x: number, y: number): Rgb {
  const i = (y * w + x) * 4;
  return { r: data[i], g: data[i + 1], b: data[i + 2] };
}

/**
 * Border samples estimate the plate color. Low variance → solid key we can matte.
 * Mean distance from the mean above ~22 usually means a busy / photographic edge.
 */
export function detectSolidBackground(data: Uint8ClampedArray, w: number, h: number): {
  key: Rgb;
  solid: boolean;
  variance: number;
} {
  const samples: Rgb[] = [];
  const step = Math.max(1, Math.floor(Math.min(w, h) / 80));
  const push = (x: number, y: number) => {
    samples.push(pixelAt(data, w, Math.min(w - 1, Math.max(0, x)), Math.min(h - 1, Math.max(0, y))));
  };
  for (let x = 0; x < w; x += step) {
    push(x, 0);
    push(x, h - 1);
    if (h > 4) {
      push(x, 1);
      push(x, h - 2);
    }
  }
  for (let y = step; y < h - step; y += step) {
    push(0, y);
    push(w - 1, y);
    if (w > 4) {
      push(1, y);
      push(w - 2, y);
    }
  }
  if (samples.length < 8) {
    return { key: { r: 255, g: 255, b: 255 }, solid: false, variance: 999 };
  }
  const key = {
    r: samples.reduce((s, p) => s + p.r, 0) / samples.length,
    g: samples.reduce((s, p) => s + p.g, 0) / samples.length,
    b: samples.reduce((s, p) => s + p.b, 0) / samples.length,
  };
  const variance = samples.reduce((s, p) => s + rgbDist(p, key), 0) / samples.length;
  // Solid studio plates (white, black, green, gray…) stay well under ~12–18.
  return { key, solid: variance <= 20, variance };
}

/** Soft alpha: 0 on the solid key color, 1 on the subject. */
function keyMatteAlpha(r: number, g: number, b: number, key: Rgb, hard = 26, soft = 52): number {
  const d = rgbDist({ r, g, b }, key);
  if (d <= hard) return 0;
  if (d >= soft) return 1;
  return (d - hard) / (soft - hard);
}

function matteSubject(img: HTMLImageElement): { canvas: HTMLCanvasElement; key: Rgb } {
  const size = naturalSize(img);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas indisponível.');
  ctx.drawImage(img, 0, 0);
  const frame = ctx.getImageData(0, 0, size.width, size.height);
  const d = frame.data;
  const { key, solid, variance } = detectSolidBackground(d, size.width, size.height);
  if (!solid) {
    throw new Error(
      `O fundo desta imagem não parece cor sólida (variação ${variance.toFixed(0)}). Use edição com IA ou uma foto com fundo uniforme.`,
    );
  }
  // Tighter band on very flat keys (pure white/black); a bit looser on mid grays.
  const flat = variance < 8;
  const hard = flat ? 22 : 30;
  const soft = flat ? 46 : 58;
  for (let i = 0; i < d.length; i += 4) {
    const a = keyMatteAlpha(d[i], d[i + 1], d[i + 2], key, hard, soft);
    d[i + 3] = Math.round(d[i + 3] * a);
  }
  ctx.putImageData(frame, 0, 0);
  return { canvas, key };
}

export type SolidProbe = {
  solid: boolean;
  variance: number;
  key: Rgb;
};

/** Probe whether a still has a solid-color border we can key (no composite). */
export async function probeSolidBackground(src: string): Promise<SolidProbe> {
  const loaded = await loadImage(src);
  try {
    const size = naturalSize(loaded.img);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas indisponível.');
    ctx.drawImage(loaded.img, 0, 0);
    const frame = ctx.getImageData(0, 0, size.width, size.height);
    return detectSolidBackground(frame.data, size.width, size.height);
  } finally {
    loaded.revoke();
  }
}

/**
 * Sample one decoded frame of a video for the solid-border heuristic.
 * `at` is a fraction of duration (0 = start, 1 = end). Logo plates often only
 * go solid near the end — probing only t=0 would miss them.
 */
export async function probeSolidVideoFrame(src: string, at = 0): Promise<SolidProbe> {
  let url = src;
  let revoke = () => {};
  if (/^https?:\/\//i.test(src)) {
    const res = await fetch(src);
    if (!res.ok) throw new Error(`Falha ao baixar vídeo (${res.status}).`);
    url = URL.createObjectURL(await res.blob());
    revoke = () => URL.revokeObjectURL(url);
  }
  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.muted = true;
  video.playsInline = true;
  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error('Falha ao carregar o vídeo.'));
      video.src = url;
    });
    const dur = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
    const frac = Math.min(1, Math.max(0, at));
    const target = dur > 0
      ? Math.min(Math.max(dur * frac, 0.05), Math.max(dur - 0.05, 0))
      : 0;
    try {
      video.currentTime = target;
      await new Promise<void>((resolve) => {
        video.onseeked = () => resolve();
        window.setTimeout(() => resolve(), 500);
      });
    } catch { /* some browsers block seek before play */ }
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) throw new Error('Vídeo sem dimensões.');
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas indisponível.');
    ctx.drawImage(video, 0, 0);
    const frame = ctx.getImageData(0, 0, w, h);
    return detectSolidBackground(frame.data, w, h);
  } finally {
    video.removeAttribute('src');
    video.load();
    revoke();
  }
}

/** Prefer a solid frame later in the clip (logo finales) over an opening photo plate. */
export async function probeSolidVideoClip(src: string): Promise<SolidProbe> {
  const points = [0.95, 0.75, 0.5, 0];
  let best: SolidProbe | undefined;
  for (const at of points) {
    try {
      const probe = await probeSolidVideoFrame(src, at);
      if (probe.solid) return probe;
      if (!best || probe.variance < best.variance) best = probe;
    } catch { /* try next sample */ }
  }
  return best || { solid: false, variance: 999, key: { r: 255, g: 255, b: 255 } };
}

/** `0xRRGGBB` for FFmpeg colorkey. */
export function rgbToFfmpegHex(key: Rgb): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `0x${h(key.r)}${h(key.g)}${h(key.b)}`;
}

/**
 * Puts `subjectUrl` (solid-color bg keyed out) over `backgroundUrl`.
 * Output size matches the subject frame; background is cover-cropped.
 */
export async function compositeSubjectOnBackground(args: {
  subjectUrl: string;
  backgroundUrl: string;
}): Promise<{ blob: Blob; width: number; height: number; keyRgb: Rgb }> {
  const subject = await loadImage(args.subjectUrl);
  const background = await loadImage(args.backgroundUrl);
  try {
    const box = naturalSize(subject.img);
    const out = document.createElement('canvas');
    out.width = box.width;
    out.height = box.height;
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('Canvas indisponível.');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const bgSize = naturalSize(background.img);
    const bg = coverRect(bgSize, box);
    ctx.drawImage(background.img, bg.x, bg.y, bg.width, bg.height);

    const { canvas: matted, key } = matteSubject(subject.img);
    const sub = containRect(box, box);
    ctx.drawImage(matted, sub.x, sub.y, sub.width, sub.height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      out.toBlob((b) => (b ? resolve(b) : reject(new Error('Falha ao exportar o composite.'))), 'image/png');
    });
    return { blob, width: box.width, height: box.height, keyRgb: key };
  } finally {
    subject.revoke();
    background.revoke();
  }
}
